import { useEffect, useMemo } from 'react';
import { useMap } from 'react-leaflet';
import 'leaflet-velocity/dist/leaflet-velocity.css';

// leaflet-velocity is a pre-ES-module Leaflet plugin: it registers
// L.velocityLayer by referencing a bare global `L`, not by importing
// leaflet itself. ES `import` statements are always hoisted above other
// code, so `import L from 'leaflet'; window.L = L; import 'leaflet-velocity';`
// would NOT reliably run in that order -- require() calls are plain
// statements executed in the order written, so they're used here instead.
const L = require('leaflet');
if (!window.L) {
  window.L = L;
}
require('leaflet-velocity');

// leaflet-velocity's underlying L.CanvasLayer schedules a requestAnimationFrame
// (in needRedraw(), for drawLayer) and a setTimeout(0) (in onAdd(), for
// _onLayerDidMove) but never cancels either in onRemove(). If the layer is
// removed before those fire -- which React StrictMode's dev-only double
// mount/cleanup/mount makes happen on essentially every render, but a plain
// unmount or toggle-off can race into the same window in production --
// Leaflet's own removeLayer() has already nulled `this._map`, and the stale
// callback crashes on `this._map.getSize()` / `this._map.containerPointToLayerPoint()`.
// Patched once here rather than forking the library for a two-line guard.
if (L.CanvasLayer && !L.CanvasLayer.prototype._nullMapGuardApplied) {
  const originalDrawLayer = L.CanvasLayer.prototype.drawLayer;
  const originalOnLayerDidMove = L.CanvasLayer.prototype._onLayerDidMove;

  L.CanvasLayer.prototype.drawLayer = function (...args) {
    if (!this._map) return undefined;
    return originalDrawLayer.apply(this, args);
  };
  L.CanvasLayer.prototype._onLayerDidMove = function (...args) {
    if (!this._map) return undefined;
    return originalOnLayerDidMove.apply(this, args);
  };
  L.CanvasLayer.prototype._nullMapGuardApplied = true;
}

// Renders an animated flow-direction particle field (leaflet-velocity) on
// the map -- used for the "Fließwege" layer instead of a static mask tile,
// since small/rare flow paths were found to visually disappear as a
// classified raster tile at low zoom, and animation itself communicates
// flow direction that a static color never could.
const VelocityLayer = ({ data, options }) => {
  const map = useMap();

  // leaflet-velocity has no "no data" value *inside* the grid. Its
  // interpolate() only returns null when a sample falls outside the raster,
  // so any cell within it yields a defined vector -- and a zero vector is a
  // perfectly defined one. A particle that steps onto a zero cell therefore
  // doesn't die and get re-seeded, it stops dead and sits there until
  // particleAge frames have elapsed, which is what made the layer drain
  // itself within a couple of seconds of every (re)start.
  //
  // VelocityFieldServer marks every cell outside the flow-path band as JSON
  // null for that reason. Turning those nulls into NaN here is what actually
  // buys the behaviour: the interpolated vector becomes NaN, so the particle
  // is moved to a NaN position, and a NaN position *is* outside the grid --
  // windy.js reads it as null, ages the particle out and re-seeds it about
  // three frames later, exactly as if it had drifted off the raster. NaN
  // can't be sent as JSON, hence the null-to-NaN pass over the payload.
  const fieldData = useMemo(() => {
    if (!data) return null;
    return data.map((component) => ({
      ...component,
      data: component.data.map((value) => (value === null ? NaN : value)),
    }));
  }, [data]);

  useEffect(() => {
    if (!fieldData) return undefined;

    const velocityLayer = L.velocityLayer({
      displayValues: false,
      data: fieldData,
      ...options,
    });
    velocityLayer.addTo(map);

    return () => {
      map.removeLayer(velocityLayer);
    };
  }, [map, fieldData]);

  return null;
};

export default VelocityLayer;
